import { FC, PropsWithChildren, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Accordion, Tabs } from '@frontend/design-system';
import { callStatusUtils } from '../../utils';
import { CallAnalysis, CallAnalysisProps } from '../call-analysis';
import { CallAnalyzeStatus } from '../call-analyze-status';
import { CallIntelFeedbackBanner } from '../call-intel-feedback-banner';
import { CallSummary, CallSummaryProps } from '../call-summary';
import { CallTranscript, CallTranscriptProps } from '../call-transcript';

interface CallIntelAccordionNamespace extends FC<PropsWithChildren> {
  Header: typeof CallIntelAccordionHeader;
  Body: typeof CallIntelAccordionBody;
}

export const CallIntelAccordion: CallIntelAccordionNamespace = ({ children }) => {
  return (
    <Accordion>
      <Accordion.Item value='callIntelAccordion'>{children}</Accordion.Item>
    </Accordion>
  );
};

const CallIntelAccordionHeader: FC<{ title?: string }> = ({ title }) => {
  const { t } = useTranslation('call-intel-core');
  return <Accordion.Header title={title || t('Call Intelligence')} />;
};

interface CallIntelAccordionBodyProps extends Omit<CallIntelTabsProps, 'callFlags' | 'isEditMode' | 'setIsEditMode'> {
  refetchCallMetadata: () => void;
}

const CallIntelAccordionBody: FC<CallIntelAccordionBodyProps> = ({ refetchCallMetadata, ...props }) => {
  const [isEditMode, setEditMode] = useState(false);
  const callFlags = callStatusUtils.getCallFlags(props.call);
  const { isFailedCall, isSkippedCall, isPoorAudio, isProcessingCall } = callFlags;

  const canShowFeedbackSection =
    !isEditMode && !isFailedCall && !isSkippedCall && !isProcessingCall && !props.isLoading;

  return (
    <Accordion.Body css={styles.accordionBody}>
      {(isPoorAudio || isSkippedCall || isFailedCall) && (
        <CallAnalyzeStatus {...callFlags} type='summary' styles={{ margin: 0 }} />
      )}
      <CallIntelTabs {...props} callFlags={callFlags} isEditMode={isEditMode} setIsEditMode={setEditMode} />
      {canShowFeedbackSection && <Divider margin={0} color={theme.colors.neutral20} />}
      {canShowFeedbackSection && props.callMetadata && (
        <div css={{ maxWidth: 'max-content' }}>
          <CallIntelFeedbackBanner
            call={props.call}
            callMetadata={props.callMetadata}
            refetchCallMetadata={refetchCallMetadata}
            isDemoAccount={props.isDemoAccount}
          />
        </div>
      )}
    </Accordion.Body>
  );
};

type CallIntelTabType = 'callSummary' | 'callTranscript' | 'callAnalysis';

interface CallIntelTabsProps extends CallSummaryProps, CallAnalysisProps, CallTranscriptProps {}

const CallIntelTabs: FC<CallIntelTabsProps> = (props) => {
  const { t } = useTranslation('callIntelInsights');
  const panelRefs = useRef<Partial<Record<CallIntelTabType, HTMLUListElement>>>({});
  const [selectedTab, setSelectedTab] = useState<CallIntelTabType>('callSummary');

  const attachRef = useCallback(
    (tab: CallIntelTabType) => (el: HTMLUListElement | null) => {
      // convert null to undefined so it matches HTMLUListElement | undefined
      panelRefs.current[tab] = el ?? undefined;
    },
    []
  );

  useEffect(() => {
    if (props.highlightedCitation && !!props.highlightedCitation.text && !!props.highlightedCitation.time) {
      setSelectedTab('callTranscript');
    }
  }, [props.highlightedCitation]);

  useLayoutEffect(() => {
    const panel = panelRefs.current[selectedTab];

    if (panel) panel.scrollTop = 0;
  }, [selectedTab]);

  return (
    <Tabs controlledTab={selectedTab} onChange={(id) => setSelectedTab(id as CallIntelTabType)}>
      <Tabs.Bar fullWidth>
        <Tabs.Tab id='callSummary'>{t('Summary')}</Tabs.Tab>
        <Tabs.Tab id='callAnalysis'>{t('Call Analysis')}</Tabs.Tab>
        <Tabs.Tab id='callTranscript'>{t('Transcript')}</Tabs.Tab>
      </Tabs.Bar>
      <Tabs.Panel controller='callSummary'>
        <CallSummary {...props} containerStyles={styles.tabContent} />
      </Tabs.Panel>
      <Tabs.Panel controller='callAnalysis'>
        <CallAnalysis {...props} containerStyles={!props.isEditMode && styles.tabContent} />
      </Tabs.Panel>
      <Tabs.Panel controller='callTranscript'>
        <CallTranscript {...props} containerStyles={styles.tabContent} ref={attachRef('callTranscript')} />
      </Tabs.Panel>
    </Tabs>
  );
};

CallIntelAccordion.Header = CallIntelAccordionHeader;
CallIntelAccordion.Body = CallIntelAccordionBody;

const styles = {
  accordionBody: css`
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  tabContent: css`
    max-height: 288px;
  `,
};
